// @ts-strict-ignore
import * as React from 'react';
import Divider from 'antd/lib/divider';
import { AppPlatform, IntegrationCapability, IntegrationType } from '../../types';
import { antGrey } from '../styles/globalStyles';
import { useSelector } from 'react-redux';
import ConnectIntegrationModal from '../ConnectIntegrationModal';
import IntegrationProviderModal from '../IntegrationProviderModal';
import { RootState } from '../../reducers/reducers';
import {
    selectAllIntegrationsForCapabilities,
    selectAppHasSelectCapabilitiesConfigured,
    selectAppPlatform,
    selectIntegrationFromIntegrationType,
    selectIncompleteCapabilitiesFromCapabilities,
    selectCompletedCapabilitiesFromCapabilities,
} from '../../selectors/selectors';
import IntegrationConfiguration from '../IntegrationConfiguration';
import { integrationCapabilitiesForPlatform } from '../../helpers/integrationHelpers';
import { difference } from 'lodash';
import { CapabilitySection } from '../setup/CapabilitySection';
import { IntegrationCard } from '../setup/IntegrationCard';
import { useAddIntegrationModal } from '../setup/useAddIntegrationModal';
import { useUpdateIntegrationModal } from '../setup/useUpdateIntegrationModal';
import { SetupStepsCallerContext } from '../../typescriptHelpers';

type IntegrationsSetupListProps = {
    appId?: string;
    orgId: string;
    relatedCapabilities: IntegrationCapability[];
    filterIntegrations?: (integration: IntegrationType) => boolean;
    shouldShowTitle?: boolean;
    shouldShowUnrelatedCapabilities?: boolean;
    platform?: AppPlatform;
    context?: SetupStepsCallerContext;
};
export const IntegrationsSetupList = (props: IntegrationsSetupListProps) => {
    const {
        appId,
        orgId,
        relatedCapabilities,
        shouldShowTitle,
        filterIntegrations,
        shouldShowUnrelatedCapabilities,
        platform,
        context,
    } = props;
    const [integrationType, setIntegrationType] = React.useState(null);
    const selectedIntegration = useSelector(
        (state: RootState) => appId && selectIntegrationFromIntegrationType(state, appId, integrationType),
    );

    //handles configuring existing integration
    const { setShowUpdateIntegrationModal, updateIntegrationModalProps } = useUpdateIntegrationModal({
        orgId,
        appId,
        selectedIntegration,
    });

    //handles modal for new integration
    const {
        setShowIntegrationModal: setShowConnectIntegrationModal,
        integrationModalProps: connectIntegrationModalProps,
    } = useAddIntegrationModal({
        orgId,
        appId,
        selectedIntegration,
        integrationType,
        setShowUpdateIntegrationModal,
        platform,
    });

    //handles modal for existing integration installation
    const {
        setShowIntegrationModal: setShowIntegrationProviderModal,
        integrationModalProps: integrationProviderModalProps,
    } = useAddIntegrationModal({
        orgId,
        appId,
        selectedIntegration,
        integrationType,
        setShowUpdateIntegrationModal,
        platform,
    });

    const onClickIntegrationShowInstallationModal = (integrationType: IntegrationType) => {
        setIntegrationType(integrationType);
        setShowConnectIntegrationModal(true);
    };

    const onClickShowProviderModal = (integrationType: IntegrationType) => {
        setIntegrationType(integrationType);
        setShowIntegrationProviderModal(true);
    };

    const completedCapabilities = useSelector((state: RootState) =>
        appId ? selectCompletedCapabilitiesFromCapabilities(state, appId, relatedCapabilities) : [],
    );
    const capabilitiesAreComplete = useSelector((state: RootState) =>
        appId ? selectAppHasSelectCapabilitiesConfigured(state, appId, relatedCapabilities) : false,
    );
    const completeRelatedIntegrations = useSelector((state: RootState) =>
        appId ? selectAllIntegrationsForCapabilities(state, appId, relatedCapabilities) : [],
    );

    const incompleteCapabilities = useSelector((state: RootState) => {
        if (!appId) {
            return relatedCapabilities;
        }
        const platform = selectAppPlatform(state, appId);
        const capabilities = integrationCapabilitiesForPlatform(platform, orgId);
        const unrelatedCapabilities = difference(capabilities, relatedCapabilities);

        return (
            capabilitiesAreComplete ?
                shouldShowUnrelatedCapabilities ?
                    selectIncompleteCapabilitiesFromCapabilities(state, appId, unrelatedCapabilities)
                :   []
            :   selectIncompleteCapabilitiesFromCapabilities(state, appId, relatedCapabilities)
        );
    });

    return (
        <>
            {completeRelatedIntegrations.map((integrationType) => {
                return (
                    <IntegrationCard
                        key={integrationType}
                        integrationType={integrationType}
                        isConnected={true}
                        onClickIntegrationShowInstallationModal={onClickIntegrationShowInstallationModal}
                        onClickShowProviderModal={onClickShowProviderModal}
                        addIntegration={connectIntegrationModalProps.addIntegration}
                        appId={appId}
                        orgId={orgId}
                    />
                );
            })}
            {completedCapabilities.length > 0 && shouldShowUnrelatedCapabilities && (
                <Divider type="horizontal" style={{ borderColor: antGrey.grey6 }} />
            )}
            {incompleteCapabilities.map((capability, idx) => {
                return (
                    <React.Fragment key={idx}>
                        <CapabilitySection
                            capability={capability}
                            onClickIntegrationShowInstallationModal={onClickIntegrationShowInstallationModal}
                            onClickShowProviderModal={onClickShowProviderModal}
                            addIntegration={connectIntegrationModalProps.addIntegration}
                            appId={appId}
                            orgId={orgId}
                            filterIntegrations={filterIntegrations}
                            shouldShowTitle={shouldShowTitle}
                            context={context}
                        />
                        {idx !== incompleteCapabilities.length - 1 && (
                            <Divider type="horizontal" style={{ borderColor: antGrey.grey6 }} />
                        )}
                    </React.Fragment>
                );
            })}

            <ConnectIntegrationModal {...connectIntegrationModalProps} appId={appId} />
            <IntegrationProviderModal
                {...integrationProviderModalProps}
                onConnectNewProvider={(integrationType) => {
                    setShowIntegrationProviderModal(false);
                    onClickIntegrationShowInstallationModal(integrationType);
                }}
            />
            {selectedIntegration && (
                <IntegrationConfiguration
                    {...updateIntegrationModalProps}
                    onClose={() => {
                        setShowUpdateIntegrationModal(false);
                    }}
                />
            )}
        </>
    );
};
